'use client'
import { createContext, use, useMemo, type ReactNode } from 'react'
import { type StoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { useLegacyStoreParams } from '@/features/shared/utils/dataFetching/legacyStoreParams'
import { useUserStoreName } from '@/features/account/services/User/hooks'
import { useViewContext } from '@/features/shared/utils/appStart/useViewContext'

export type RetailerContextType = {
  storeName: string | undefined
  storeParams: StoreParams
  isGlobalContext: boolean
  /** Re-provide this store context inside dialogs (which render in a portal outside the provider tree). */
  propagateToDialogs: boolean
}

const RetailerContext = createContext<RetailerContextType | undefined>(undefined)

export const GlobalRetailerContextProvider = ({
  children,
  globalStoreParams,
}: {
  children: ReactNode
  globalStoreParams: StoreParams
}) => {
  const value = useMemo(() => {
    return {
      isGlobalContext: true,
      storeParams: globalStoreParams,
      storeName: undefined,
      propagateToDialogs: true,
    }
  }, [globalStoreParams])

  return <RetailerContext value={value}>{children}</RetailerContext>
}
export const PageRetailerContextProvider = ({
  children,
  isRetailerShoppingContext,
}: {
  children: ReactNode
  isRetailerShoppingContext: boolean
}) => {
  const storeName = useUserStoreName()
  const userStoreParams = useLegacyStoreParams()
  const value = useMemo(() => {
    return isRetailerShoppingContext
      ? {
          isGlobalContext: false,
          storeParams: userStoreParams,
          storeName,
          propagateToDialogs: false,
        }
      : undefined
  }, [userStoreParams, isRetailerShoppingContext, storeName])

  useViewContext(value)

  return <RetailerContext value={value}>{children}</RetailerContext>
}

export const useRetailerContext = () => {
  return use(RetailerContext)
}

/** Re-provides the opening page's store context inside a dialog's portal. */
export const DialogRetailerContextProvider = ({
  children,
  value,
}: {
  children: ReactNode
  value: RetailerContextType | undefined
}) => {
  if (!value) return children
  return <RetailerContext value={value}>{children}</RetailerContext>
}

/**
 * Overrides the ambient store context with a shared list's own store so a
 * collaborator's reads/writes target the list's store, not their own. No-op
 * when the list lacks a full store context.
 */
export const CollaborativeListStoreContextProvider = ({
  children,
  listStoreContext,
}: {
  children: ReactNode
  listStoreContext: {
    store_id?: string
    metro_id?: string
    store_location_id?: string
  }
}) => {
  const storeName = useUserStoreName()
  const userStoreParams = useLegacyStoreParams()
  const parentContext = useRetailerContext()

  const value = useMemo(() => {
    const store_id = Number(listStoreContext.store_id) || null
    const metro_id = Number(listStoreContext.metro_id) || null
    const store_location_id = Number(listStoreContext.store_location_id) || null

    if (!store_id || !metro_id || !store_location_id) return parentContext

    return {
      isGlobalContext: false,
      storeName,
      propagateToDialogs: true,
      storeParams: {
        ...userStoreParams,
        store_id,
        metro_id,
        store_location_id,
      },
    }
  }, [listStoreContext, userStoreParams, storeName, parentContext])

  return <RetailerContext value={value}>{children}</RetailerContext>
}
